import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
    Autocomplete,
    Backdrop,
    Box,
    Button,
    CircularProgress,
    Grid,
    IconButton,
    Stack,
    TextField,
    Typography,
    Chip,
    Paper,
    InputAdornment,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Card,
    CardContent,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import {
    Mic as MicIcon,
    MicOff as MicOffIcon,
    Send as SendIcon,
    PersonSearch as PersonSearchIcon,
    Draw as DrawIcon,
    Clear as ClearIcon,
    ArrowBack as ArrowBackIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    PhotoCamera as PhotoCameraIcon,
    AttachFile as AttachFileIcon,
    ExpandMore as ExpandMoreIcon,
    ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { initializeDB } from '../../../config/db';
import { JOB_STORE, PNEUMATIC_TEST_STORE, EMPLOYEE_STORE } from '../../../config/constants';
import useDictation from '../../../hooks/useDictation';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import CameraCapture from '../../shared/CameraCapture';
import { generateUUID } from '../../../utils/uuid';
import { hasSignatureContent } from '../../../utils/utils';
import axios from 'axios';
import { URL } from '../../../config/constants';
import Swal from 'sweetalert2';

const STATUS_DRAFT = 'draft';
const STATUS_SUBMITTED = 'submitted';

export default function PneumaticTestForm() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [jobs, setJobs] = useState([]);
    const [employees, setEmployees] = useState([]);

    // Get current user info
    const userFullName = localStorage.getItem('employeeFullName') || '';
    const userId = localStorage.getItem('employeeId') || '';

    const [form, setForm] = useState({
        uuid: generateUUID(),
        jobCode: '',
        jobName: '',
        customer: '',
        location: '',
        testDate: dayjs().format('YYYY-MM-DD'),
        contractor: '',
        refStandard: '',
        docNo: '',
        supportingDoc: '',
        designPressure: '',
        ambientTemperature: '',
        testMediaTemperature: '',
        areaLocation: '',
        testItems: [{
            itemNo: 1,
            dwgNo: '',
            lineNo: '',
            from: '',
            to: '',
            pipeDia: '',
            pipeMat: '',
            duration: '',
            testPres: '',
            resultRemarks: '',
            pictures: []
        }],
        signatureType: 'internal', // 'internal' or 'client'
        internalEmployeeId: userId,
        internalEmployeeName: userFullName,
        internalSignature: '',
        internalSignatureDate: dayjs().format('YYYY-MM-DD'),
        clientName: '',
        clientSignature: '',
        clientSignatureDate: dayjs().format('YYYY-MM-DD'),
        status: STATUS_DRAFT,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
    });
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [cameraTarget, setCameraTarget] = useState(null);
    const [selectedItemIndex, setSelectedItemIndex] = useState(null);

    const saveTimer = useRef(null);
    const sigPadRef = useRef(null);

    // Dictation for remarks
    const { isSupported: speechSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({ lang: 'en-US' });
    const [dictationItemIndex, setDictationItemIndex] = useState(null);

    // Sort jobs by Job number in descending order
    const sortedJobs = useMemo(() => {
        return [...jobs].sort((a, b) => {
            const jobA = parseInt(a.Job) || 0;
            const jobB = parseInt(b.Job) || 0;
            return jobB - jobA;
        });
    }, [jobs]);

    // Load jobs and employees from IndexedDB
    useEffect(() => {
        (async () => {
            try {
                const db = await initializeDB();

                // Load jobs
                if (db.objectStoreNames.contains(JOB_STORE)) {
                    const tx = db.transaction(JOB_STORE, 'readonly');
                    const store = tx.objectStore(JOB_STORE);
                    const allJobs = await store.getAll();
                    const options = (allJobs || [])
                        .filter(j => j && (j.Job || j.JobName))
                        .map(j => ({ ...j, label: `${j.Job || ''} - ${j.JobName || ''}`.trim() }));
                    setJobs(options);
                }

                // Load employees
                if (db.objectStoreNames.contains(EMPLOYEE_STORE)) {
                    const tx2 = db.transaction(EMPLOYEE_STORE, 'readonly');
                    const store2 = tx2.objectStore(EMPLOYEE_STORE);
                    const allEmployees = await store2.getAll();
                    setEmployees(allEmployees || []);
                }
            } catch (e) {
                console.error('Error loading data:', e);
            }
        })();
    }, []);

    // Load existing draft by uuid if provided
    useEffect(() => {
        const uuid = searchParams.get('uuid');
        if (uuid) {
            loadDraft(uuid);
        }
    }, [searchParams]);

    const loadDraft = async (uuid) => {
        try {
            const db = await initializeDB();
            const tx = db.transaction(PNEUMATIC_TEST_STORE, 'readonly');
            const store = tx.objectStore(PNEUMATIC_TEST_STORE);
            const draft = await store.get(uuid);
            if (draft) {
                // If form is already submitted, redirect to list
                if (draft.status === STATUS_SUBMITTED) {
                    navigate('/piping/forms/pneumatic-test/list');
                    return;
                }
                setForm(draft);
                setSubmitted(false);
            }
        } catch (e) {
            console.error('Error loading draft:', e);
        }
    };

    // Auto-save function
    const queueSave = (data) => {
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            await saveDraft(data);
        }, 500);
    };

    const saveDraft = async (data) => {
        if (submitted) return;
        try {
            const db = await initializeDB();
            const tx = db.transaction(PNEUMATIC_TEST_STORE, 'readwrite');
            const store = tx.objectStore(PNEUMATIC_TEST_STORE);
            const saveData = {
                ...data,
                updatedAt: new Date().toISOString()
            };
            await store.put(saveData);
            console.log('Draft saved:', saveData.uuid);
        } catch (e) {
            console.error('Error saving draft:', e);
        }
    };

    // Handle form field changes
    const handleFieldChange = (field, value) => {
        const updated = { ...form, [field]: value };
        setForm(updated);
        queueSave(updated);
    };

    // Handle job selection
    const handleJobChange = (event, newValue) => {
        if (newValue) {
            const updated = {
                ...form,
                jobCode: newValue.Job || '',
                jobName: newValue.JobName || '',
                customer: newValue.Customer || '',
                location: newValue.location || newValue.Location || ''
            };
            setForm(updated);
            queueSave(updated);
        } else {
            const updated = {
                ...form,
                jobCode: '',
                jobName: '',
                customer: '',
                location: ''
            };
            setForm(updated);
            queueSave(updated);
        }
    };

    // Handle test item operations
    const addTestItem = () => {
        const newItem = {
            itemNo: form.testItems.length + 1,
            dwgNo: '',
            lineNo: '',
            from: '',
            to: '',
            pipeDia: '',
            pipeMat: '',
            duration: '',
            testPres: '',
            resultRemarks: '',
            pictures: []
        };
        const updated = {
            ...form,
            testItems: [...form.testItems, newItem]
        };
        setForm(updated);
        queueSave(updated);
    };

    const removeTestItem = (index) => {
        // Prevent deleting the last item
        if (form.testItems.length <= 1) {
            return;
        }
        const updated = {
            ...form,
            testItems: form.testItems.filter((_, i) => i !== index).map((item, idx) => ({
                ...item,
                itemNo: idx + 1
            }))
        };
        setForm(updated);
        queueSave(updated);
    };

    const duplicateTestItem = (index) => {
        const itemToCopy = form.testItems[index];
        const newItem = {
            ...itemToCopy,
            itemNo: form.testItems.length + 1,
            resultRemarks: '', // Don't copy remarks
            pictures: [] // Don't copy photos
        };
        const updated = {
            ...form,
            testItems: [...form.testItems, newItem]
        };
        setForm(updated);
        queueSave(updated);
    };

    const updateTestItem = (index, field, value) => {
        const updated = {
            ...form,
            testItems: form.testItems.map((item, i) =>
                i === index ? { ...item, [field]: value } : item
            )
        };
        setForm(updated);
        queueSave(updated);
    };

    // Handle dictation for remarks
    const handleStartDictation = (index) => {
        setDictationItemIndex(index);
        startDictation({
            onResult: (transcript) => {
                setForm(currentForm => {
                    const currentText = currentForm.testItems[index]?.resultRemarks || '';
                    const needsSpace = currentText && !currentText.endsWith(' ');
                    const newText = currentText + (needsSpace ? ' ' : '') + transcript + ' ';
                    const updated = {
                        ...currentForm,
                        testItems: currentForm.testItems.map((item, idx) =>
                            idx === index ? { ...item, resultRemarks: newText } : item
                        )
                    };
                    queueSave(updated);
                    return updated;
                });
            }
        });
    };

    const handleStopDictation = () => {
        stopDictation();
        setDictationItemIndex(null);
    };

    // Handle photo capture
    const handleCapturePhoto = (index) => {
        setSelectedItemIndex(index);
        setCameraTarget({ type: 'testItem', index });
    };

    const handlePhotoCapture = (photoData) => {
        if (cameraTarget && cameraTarget.type === 'testItem') {
            const index = cameraTarget.index;
            const pictures = [...(form.testItems[index]?.pictures || [])];
            pictures.push({
                id: generateUUID(),
                dataUrl: photoData.dataUrl,
                name: photoData.name,
                timestamp: photoData.timestamp
            });
            updateTestItem(index, 'pictures', pictures);
        }
        setCameraTarget(null);
    };

    const handleRemovePhoto = (itemIndex, photoId) => {
        const pictures = form.testItems[itemIndex]?.pictures.filter(p => p.id !== photoId) || [];
        updateTestItem(itemIndex, 'pictures', pictures);
    };

    // Handle signature section
    const handleSignatureTypeChange = (event) => {
        const newType = event.target.value;
        const updated = {
            ...form,
            signatureType: newType
        };
        setForm(updated);
        queueSave(updated);
    };

    const handleSelectEmployee = (employee) => {
        const updated = {
            ...form,
            internalEmployeeId: employee.id,
            internalEmployeeName: employee.FullName || employee.name || ''
        };
        setForm(updated);
        queueSave(updated);
        setEmployeeModalOpen(false);
    };

    const handleSignatureAccept = () => {
        if (!hasSignatureContent(sigPadRef)) {
            return;
        }

        const dataUrl = sigPadRef.current.toDataURL();
        const dateField = form.signatureType === 'internal' ? 'internalSignatureDate' : 'clientSignatureDate';
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: dataUrl,
            [dateField]: dayjs().format('YYYY-MM-DD')
        };
        setForm(updated);
        queueSave(updated);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => {
        const sigField = form.signatureType === 'internal' ? 'internalSignature' : 'clientSignature';
        const updated = {
            ...form,
            [sigField]: ''
        };
        setForm(updated);
        queueSave(updated);
    };

    // Form validation
    const validate = () => {
        const newErrors = {};
        if (!form.jobCode) newErrors.jobCode = 'Job is required';

        if (form.signatureType === 'internal') {
            if (!form.internalSignature) newErrors.internalSignature = 'Signature is required';
            if (!form.internalEmployeeName) newErrors.internalEmployeeName = 'Employee name is required';
        } else {
            if (!form.clientSignature) newErrors.clientSignature = 'Signature is required';
            if (!form.clientName) newErrors.clientName = 'Client name is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Submit form
    const handleSubmit = async () => {
        if (!validate()) {
            await Swal.fire({
                icon: 'warning',
                title: 'Validation Error',
                text: 'Please fill in all required fields',
                confirmButtonColor: '#ff9800'
            });
            return;
        }

        setSaving(true);
        try {
            const submitData = {
                ...form,
                status: STATUS_SUBMITTED,
                updatedAt: new Date().toISOString()
            };

            // Save to API
            const response = await axios.post(`${URL}/pneumatic-test/submit`, submitData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.data.success) {
                // Update local IndexedDB
                const db = await initializeDB();
                const tx = db.transaction(PNEUMATIC_TEST_STORE, 'readwrite');
                const store = tx.objectStore(PNEUMATIC_TEST_STORE);
                await store.put(submitData);

                setForm(submitData);
                setSubmitted(true);
                setSaving(false);

                const result = await Swal.fire({
                    icon: 'success',
                    title: 'Success!',
                    text: 'Pneumatic Test form submitted successfully!',
                    confirmButtonColor: '#4caf50'
                });

                if (result.isConfirmed || result.isDismissed) {
                    navigate('/piping/forms/pneumatic-test/list');
                }
            } else {
                setSaving(false);
                throw new Error(response.data.message || 'Failed to submit form');
            }
        } catch (e) {
            console.error('Error submitting form:', e);
            setSaving(false);
            await Swal.fire({
                icon: 'error',
                title: 'Error',
                text: e.response?.data?.message || 'Failed to submit form. Please try again.',
                confirmButtonColor: '#f44336'
            });
        }
    };

    const currentSignature = form.signatureType === 'internal' ? form.internalSignature : form.clientSignature;

    return (
        <Box sx={{
            minHeight: 'calc(100vh - 60px)',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            flexDirection: 'column'
        }}>
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: 1,
                    borderColor: 'divider',
                    backgroundColor: 'background.paper'
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <IconButton onClick={() => navigate('/piping/forms/pneumatic-test/list')} color="primary" size="large">
                        <ArrowBackIcon />
                    </IconButton>
                    <Box>
                        <Typography variant="h5">Pneumatic Test</Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                            {form.jobCode ? `Job: ${form.jobCode} - ${form.jobName}` : 'New Test Form'}
                        </Typography>
                    </Box>
                </Box>
                <Box display="flex" gap={1}>
                    {form.status === STATUS_DRAFT && (
                        <Chip label="Draft" color="default" size="small" />
                    )}
                    {form.status === STATUS_SUBMITTED && (
                        <Chip label="Submitted" color="success" size="small" />
                    )}
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 1100
                    }}
                >
                    {/* Job Information & Test Details - Combined Accordion */}
                    <Accordion
                        defaultExpanded
                        elevation={1}
                        sx={{
                            mb: 0.5,
                            mt: 0,
                            '&.Mui-expanded': {
                                margin: 0,
                                mb: 0.5
                            }
                        }}
                    >
                        <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                                minHeight: '36px !important',
                                height: '36px',
                                py: 0,
                                '& .MuiAccordionSummary-content': {
                                    margin: '6px 0 !important'
                                }
                            }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Job Information & Test Details</Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 1.5, pt: 1 }}>
                            <Stack spacing={1.5}>
                                {/* Job Selection */}
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                        <Grid item xs={12} sm={6}>
                                            <Autocomplete
                                                options={sortedJobs}
                                                getOptionLabel={(option) => option.label || ''}
                                                value={sortedJobs.find(j => j.Job === form.jobCode) || null}
                                                onChange={handleJobChange}
                                                disabled={submitted}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        size="small"
                                                        label="Job *"
                                                        error={!!errors.jobCode}
                                                        helperText={errors.jobCode}
                                                    />
                                                )}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Area/Location"
                                                value={form.areaLocation}
                                                onChange={(e) => handleFieldChange('areaLocation', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>

                                {/* Test Details */}
                                <Box>
                                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Date"
                                                type="date"
                                                value={form.testDate}
                                                onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                                onChange={(e) => handleFieldChange('testDate', e.target.value)}
                                                disabled={submitted}
                                                InputLabelProps={{ shrink: true }}
                                                InputProps={{
                                                    style: { cursor: submitted ? 'default' : 'pointer' }
                                                }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Contractor"
                                                value={form.contractor}
                                                onChange={(e) => handleFieldChange('contractor', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Ref. Standard"
                                                value={form.refStandard}
                                                onChange={(e) => handleFieldChange('refStandard', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Doc. No."
                                                value={form.docNo}
                                                onChange={(e) => handleFieldChange('docNo', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Supporting Doc"
                                                value={form.supportingDoc}
                                                onChange={(e) => handleFieldChange('supportingDoc', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Design Pressure"
                                                value={form.designPressure}
                                                onChange={(e) => handleFieldChange('designPressure', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Ambient Temperature"
                                                value={form.ambientTemperature}
                                                onChange={(e) => handleFieldChange('ambientTemperature', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6} md={4}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                label="Test Media Temperature"
                                                value={form.testMediaTemperature}
                                                onChange={(e) => handleFieldChange('testMediaTemperature', e.target.value)}
                                                disabled={submitted}
                                            />
                                        </Grid>
                                    </Grid>
                                </Box>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    {/* Test Items */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Test Items</Typography>
                                {!submitted && (
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={addTestItem}
                                        size="small"
                                    >
                                        Add Item
                                    </Button>
                                )}
                            </Box>
                            {form.testItems.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
                                    No test items added yet. Click "Add Item" to get started.
                                </Typography>
                            ) : (
                                <Stack spacing={1.25}>
                                {form.testItems.map((item, index) => (
                                    <Card key={index} variant="outlined" sx={{ bgcolor: '#fafafa' }}>
                                        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                                <Typography variant="subtitle2" fontWeight={600}>
                                                    Item #{item.itemNo}
                                                </Typography>
                                                {!submitted && (
                                                    <Box display="flex" gap={1.5}>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => duplicateTestItem(index)}
                                                            color="primary"
                                                            title="Duplicate item"
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => removeTestItem(index)}
                                                            color="error"
                                                            title="Delete item"
                                                            disabled={form.testItems.length <= 1}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </Box>
                                                )}
                                            </Box>

                                            <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }}>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Dwg No."
                                                        size="small"
                                                        value={item.dwgNo}
                                                        onChange={(e) => updateTestItem(index, 'dwgNo', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Line No"
                                                        size="small"
                                                        value={item.lineNo}
                                                        onChange={(e) => updateTestItem(index, 'lineNo', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="From"
                                                        size="small"
                                                        value={item.from}
                                                        onChange={(e) => updateTestItem(index, 'from', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="To"
                                                        size="small"
                                                        value={item.to}
                                                        onChange={(e) => updateTestItem(index, 'to', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Pipe Dia."
                                                        size="small"
                                                        value={item.pipeDia}
                                                        onChange={(e) => updateTestItem(index, 'pipeDia', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Pipe Mat."
                                                        size="small"
                                                        value={item.pipeMat}
                                                        onChange={(e) => updateTestItem(index, 'pipeMat', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Duration"
                                                        size="small"
                                                        value={item.duration}
                                                        onChange={(e) => updateTestItem(index, 'duration', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>
                                                <Grid item xs={12} sm={6} md={4}>
                                                    <TextField
                                                        fullWidth
                                                        label="Test Pres."
                                                        size="small"
                                                        value={item.testPres}
                                                        onChange={(e) => updateTestItem(index, 'testPres', e.target.value)}
                                                        disabled={submitted}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <TextField
                                                        fullWidth
                                                        size="small"
                                                        label="Result/Remarks"
                                                        multiline
                                                        rows={3}
                                                        value={item.resultRemarks}
                                                        onChange={(e) => updateTestItem(index, 'resultRemarks', e.target.value)}
                                                        disabled={submitted}
                                                        InputProps={{
                                                            endAdornment: !submitted && speechSupported && (
                                                                <InputAdornment position="end">
                                                                    <IconButton
                                                                        onClick={() =>
                                                                            dictationItemIndex === index
                                                                                ? handleStopDictation()
                                                                                : handleStartDictation(index)
                                                                        }
                                                                        color={dictationItemIndex === index ? 'error' : 'default'}
                                                                    >
                                                                        {dictationItemIndex === index ? <MicOffIcon /> : <MicIcon />}
                                                                    </IconButton>
                                                                </InputAdornment>
                                                            )
                                                        }}
                                                    />
                                                </Grid>

                                                <Grid item xs={12}>
                                                    <Box>
                                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                Pictures ({item.pictures?.length || 0})
                                                            </Typography>
                                                            {!submitted && (
                                                                <Button
                                                                    variant="outlined"
                                                                    size="small"
                                                                    startIcon={<PhotoCameraIcon />}
                                                                    onClick={() => handleCapturePhoto(index)}
                                                                    sx={{ fontSize: '0.75rem', py: 0.5 }}
                                                                >
                                                                    Add Photo
                                                                </Button>
                                                            )}
                                                        </Box>
                                                        <Box display="flex" gap={0.5} flexWrap="wrap">
                                                            {item.pictures?.map((photo) => (
                                                                <Box key={photo.id} position="relative" display="inline-block">
                                                                    <img
                                                                        src={photo.dataUrl}
                                                                        alt=""
                                                                        style={{
                                                                            width: 60,
                                                                            height: 60,
                                                                            objectFit: 'cover',
                                                                            borderRadius: 4,
                                                                            border: '1px solid #ddd'
                                                                        }}
                                                                    />
                                                                    {!submitted && (
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleRemovePhoto(index, photo.id)}
                                                                            sx={{
                                                                                position: 'absolute',
                                                                                top: -6,
                                                                                right: -6,
                                                                                bgcolor: 'error.main',
                                                                                color: 'white',
                                                                                p: 0.25,
                                                                                '&:hover': { bgcolor: 'error.dark' }
                                                                            }}
                                                                        >
                                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                                        </IconButton>
                                                                    )}
                                                                </Box>
                                                            ))}
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                ))}
                            </Stack>
                            )}
                            {!submitted && form.testItems.length > 0 && (
                                <Box display="flex" justifyContent="flex-end" mt={1.5}>
                                    <Button
                                        variant="contained"
                                        startIcon={<AddIcon />}
                                        onClick={addTestItem}
                                        size="small"
                                    >
                                        Add Item
                                    </Button>
                                </Box>
                            )}
                        </CardContent>
                    </Card>

                    {/* Signature Section */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} gap={1.5} mb={1.25}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Signature</Typography>
                                <ToggleButtonGroup
                                    color="primary"
                                    value={form.signatureType}
                                    exclusive
                                    onChange={(e, newValue) => newValue && handleSignatureTypeChange({ target: { value: newValue } })}
                                    disabled={submitted}
                                    size="small"
                                >
                                    <ToggleButton value="internal" sx={{ px: 2 }}>
                                        Internal
                                    </ToggleButton>
                                    <ToggleButton value="client" sx={{ px: 2 }}>
                                        Client/Inspector
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>

                        {form.signatureType === 'internal' ? (
                            <Box>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Employee Name *"
                                            value={form.internalEmployeeName}
                                            onClick={() => !submitted && setEmployeeModalOpen(true)}
                                            InputProps={{
                                                readOnly: true,
                                                style: { cursor: submitted ? 'default' : 'pointer' },
                                                endAdornment: !submitted && (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setEmployeeModalOpen(true)} size="small">
                                                            <PersonSearchIcon fontSize="small" />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                            error={!!errors.internalEmployeeName}
                                            helperText={errors.internalEmployeeName}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.internalSignatureDate}
                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                            onChange={(e) => handleFieldChange('internalSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.internalSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.internalSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => !submitted && setSignatureModalOpen(true)}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleClearSignature}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => setSignatureModalOpen(true)}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.internalSignature && (
                                            <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                {errors.internalSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        ) : (
                            <Box>
                                <Grid container rowSpacing={1} columnSpacing={{ xs: 1, md: 1.5 }} alignItems="center">
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Client/Inspector Name *"
                                            value={form.clientName}
                                            onChange={(e) => handleFieldChange('clientName', e.target.value)}
                                            disabled={submitted}
                                            error={!!errors.clientName}
                                            helperText={errors.clientName}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            label="Date"
                                            type="date"
                                            value={form.clientSignatureDate}
                                            onClick={(e) => !submitted && e.target.showPicker && e.target.showPicker()}
                                            onChange={(e) => handleFieldChange('clientSignatureDate', e.target.value)}
                                            disabled={submitted}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                style: { cursor: submitted ? 'default' : 'pointer' }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Box display="flex" justifyContent="center" alignItems="center">
                                            {form.clientSignature ? (
                                                <Box position="relative" display="inline-block">
                                                    <img
                                                        src={form.clientSignature}
                                                        alt="Signature"
                                                        style={{
                                                            maxWidth: 200,
                                                            height: 60,
                                                            border: '1px solid #ddd',
                                                            borderRadius: 4,
                                                            cursor: submitted ? 'default' : 'pointer'
                                                        }}
                                                        onClick={() => !submitted && setSignatureModalOpen(true)}
                                                        title={submitted ? '' : 'Click to edit signature'}
                                                    />
                                                    {!submitted && (
                                                        <IconButton
                                                            size="small"
                                                            onClick={handleClearSignature}
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                                                        >
                                                            <ClearIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            ) : (
                                                <Button
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<DrawIcon />}
                                                    onClick={() => setSignatureModalOpen(true)}
                                                    disabled={submitted}
                                                >
                                                    Add Signature
                                                </Button>
                                            )}
                                        </Box>
                                        {errors.clientSignature && (
                                            <Typography color="error" variant="caption" display="block" mt={0.5} textAlign="center">
                                                {errors.clientSignature}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        )}
                        </CardContent>
                    </Card>

                    {/* Submit Button */}
                    {!submitted && (
                        <Box display="flex" justifyContent="center" sx={{ mt: 2, mb: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleSubmit}
                                disabled={saving}
                                startIcon={saving ? <CircularProgress size={20} /> : <SendIcon />}
                                sx={{ minWidth: 200 }}
                            >
                                {saving ? 'Submitting...' : 'Submit'}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Modals */}
            <SelectSingleEmployeeModal
                open={employeeModalOpen}
                onClose={() => setEmployeeModalOpen(false)}
                onSelect={handleSelectEmployee}
                employees={employees}
            />

            <SignatureModalWithAuto
                open={signatureModalOpen}
                onClose={() => setSignatureModalOpen(false)}
                sigPadRef={sigPadRef}
                employeeName={form.signatureType === 'internal' ? form.internalEmployeeName : form.clientName}
                onAccept={handleSignatureAccept}
                existingSignature={form.signatureType === 'internal' ? form.internalSignature : form.clientSignature}
            />

            <CameraCapture
                open={!!cameraTarget}
                onClose={() => setCameraTarget(null)}
                onCapture={handlePhotoCapture}
            />

            <Backdrop open={saving} sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, color: '#fff' }}>
                <CircularProgress color="inherit" />
            </Backdrop>
        </Box>
    );
}
